import { ActionIcon, Badge, Checkbox, CopyButton, Menu, Skeleton, Text, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconClipboardCopy,
  IconCopy,
  IconCopyPlus,
  IconDots,
  IconEdit,
  IconExclamationCircle,
  IconExternalLink,
  IconGitBranch,
  IconPlayerPlay,
  IconStrikethrough,
  IconTrash,
} from '@tabler/icons-react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { cn } from '~/common/string/classname';
import { TestTagCombobox } from '~/components/test/TestTagCombobox';
import { TestTypeIcon } from '~/components/test/TestTypeIcon';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { TestListItem } from '~/server/api/routers/test';
import type { UserFlowType } from '~/server/api/routers/userflow';
import { api } from '~/trpc-extra/api';
import CreatedBy from '../../test/CreatedBy';
import { TestSuiteLabel } from '../../test/TestSuiteLabel';
import TimeAgo from '../../time/TimeAgo';
import type { RouterOutputs } from '~/trpc-extra/api';
import { openSingleTestRunModal } from '../../test/SingleTestRunModal';

function NameCell({ row }: { row: Row<TestListItem> }) {
  const projectId = useProjectId();
  const router = useRouter();

  return (
    <Link
      href={{
        pathname: router.pathname,
        query: { ...router.query, testDrawer: row.original.id },
      }}
      as={`/project/${projectId}/test/${row.original.id}`}
      className={cn('cursor-pointer hover:text-blue-600 dark:hover:text-blue-400', {
        'text-[var(--mantine-color-dimmed)]': row.original.skip,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1">
        {row.original.skip ?
          <IconStrikethrough
            size={16}
            className="shrink-0"
          />
        : undefined}
        <span className="break-words">{row.original.name}</span>
      </div>
    </Link>
  );
}

function ActionsCell({ row }: { row: Row<TestListItem> }) {
  const utils = api.useUtils();
  const projectId = useProjectId();
  const clipboard = useClipboard();
  const router = useRouter();
  const { mutateAsync: duplicateTest } = api.test.duplicate.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'teal',
        message: 'Duplicating test',
        icon: <IconCopy />,
      });
    },
    onError: (e) => {
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Unable to Duplicate',
        message: 'Please contact support',
        icon: <IconExclamationCircle />,
      });
    },
    onSettled: async () => {
      await utils.test.listTests.invalidate();
    },
  });
  const { mutateAsync: getTestDefinition } = api.test.getTestDefinition.useMutation();
  const { mutate: deleteTest } = api.test.delete.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'gray',
        title: 'Deleting test',
        message: 'Please wait...',
        icon: <IconTrash size="1rem" />,
      });
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Complete',
        message: 'Test Deleted',
        icon: <IconTrash size="1rem" />,
      });
      await utils.test.listTests.invalidate();
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: 'Issue while deleting',
        icon: <IconExclamationCircle size="1rem" />,
      });
    },
    onSettled: () => {
      if (row.getIsSelected()) {
        row.toggleSelected();
      }
    },
  });
  const { mutateAsync: createDraftForTest } = api.testDraft.forkTrunk.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Creating a new draft copy...', color: 'gray' });
    },
    onSuccess: async (testDraft) => {
      await router.push(`/project/${projectId}/testDraft/${testDraft.id}?fromTest=true`);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error start editing', color: 'red' });
    },
  });

  return (
    <Menu
      shadow="md"
      width={175}
      transitionProps={{ duration: 0 }}
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={(e) => e.stopPropagation()}
        >
          <IconDots size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <Menu.Label>Quick Actions</Menu.Label>
        <Menu.Item
          leftSection={<IconPlayerPlay size={14} />}
          disabled={!row.original.trunkContentId}
          onClick={(e) => {
            e.stopPropagation();
            if (!projectId || !row.original.trunkContentId) {
              return;
            }
            openSingleTestRunModal({
              projectId,
              testId: row.original.id,
              testName: row.original.name,
            });
          }}
        >
          Run Test
        </Menu.Item>
        <Menu.Item
          component={Link}
          href={`/project/${projectId}/test/${row.original.id}`}
          leftSection={<IconExternalLink size={14} />}
        >
          Open
        </Menu.Item>
        {/* TODO: Add rename */}
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          onClick={async () => {
            await createDraftForTest({ testId: row.original.id });
          }}
        >
          Edit Test
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCopyPlus size={14} />}
          onClick={async () => await duplicateTest({ id: row.original.id })}
        >
          Duplicate
        </Menu.Item>
        <CopyButton value={row.original.id}>
          {({ copy }) => (
            <Menu.Item
              leftSection={<IconClipboardCopy size={14} />}
              onClick={copy}
            >
              Copy ID
            </Menu.Item>
          )}
        </CopyButton>
        <Menu.Item
          leftSection={<IconCopy size={14} />}
          onClick={async (e) => {
            e.stopPropagation();
            try {
              const testDefinition = await getTestDefinition({
                id: row.original.id,
                projectId: projectId || '',
              });
              const testDefinitionJSON = JSON.stringify(testDefinition);
              clipboard.copy(testDefinitionJSON);
              notifications.show({
                title: 'Test definition copied successfully',
                message: 'The test definition was copied to your clipboard',
              });
            } catch (err) {
              notifications.show({
                color: 'red',
                title: 'Error',
                message: 'Error copying test definition',
              });
            }
          }}
        >
          Copy Definition
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() =>
            modals.openConfirmModal({
              title: `Delete Test?`,
              children: (
                <Text>
                  This will permanently delete <span className="font-bold">{row.original.name}</span>
                </Text>
              ),
              labels: { confirm: 'Delete', cancel: 'Cancel' },
              onConfirm: () => deleteTest({ ids: [row.original.id] }),
              confirmProps: { color: 'red' },
              centered: true,
              size: 'xs',
            })
          }
        >
          Delete
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

// Will be deprecated. Temporarily share this with testDraftColumns.tsx
export const UserFlowsCell = ({ userFlows }: { userFlows: TestListItem['userFlows'] }) => {
  const router = useRouter();
  const projectId = useProjectId();
  return (
    <Tooltip label={userFlows.map((userFlow) => userFlow.name).join(', ')}>
      <Badge
        variant="default"
        className="cursor-pointer font-semibold normal-case"
        onClick={() => router.push(`/project/${projectId}/coverage`)}
      >
        <div className="flex items-center gap-1">
          <IconGitBranch size={12} />
          {userFlows.length} flow{userFlows.length === 1 ? '' : 's'}
        </div>
      </Badge>
    </Tooltip>
  );
};

export const testColumns = ({
  userFlows,
  tags,
  tests,
}: {
  userFlows: UserFlowType[];
  tags: RouterOutputs['tag']['list'];
  tests: TestListItem[];
}): ColumnDef<TestListItem>[] => {
  // Object spread has type issues, needed to define userflow column separately and conditionally render
  const userFlowsColumn: ColumnDef<TestListItem> = {
    accessorFn: (row: TestListItem) => row.userFlows,
    id: 'userFlows',
    header: 'User Flows',
    size: 120,
    cell: ({ row }) => {
      return row.original.userFlows.length > 0 && <UserFlowsCell userFlows={row.original.userFlows} />;
    },
  };

  const baseColumns: ColumnDef<TestListItem>[] = [
    {
      id: 'select',
      enableSorting: false,
      enableHiding: false,
      size: 35,
      header: ({ table }) => (
        <Checkbox
          role="none"
          size="xs"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          size="xs"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => {
            // Default to onRowClick if row is not selectable
            if (row.getCanSelect()) {
              e.stopPropagation();
            }
          }}
        />
      ),
      meta: {
        skeleton: () => (
          <Skeleton
            height={28}
            className="opacity-0"
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.name,
      size: 300,
      header: 'Name',
      id: 'name',
      cell: ({ row }) => <NameCell row={row} />,
      enableHiding: false,
      meta: {
        skeleton: () => (
          <div className="flex gap-2">
            <Skeleton
              height={20}
              width={`${Math.random() * 200 + 200}px`}
            />
            <Skeleton
              height={16}
              width={70}
              radius="xl"
              hidden={Math.random() > 0.5}
            />
          </div>
        ),
      },
    },
    {
      accessorFn: (row) => row.testType,
      id: 'testType',
      size: 70,
      header: 'Type',
      enableColumnFilter: true,
      cell: ({ row }) => <TestTypeIcon type={row.original.testType} />,
      sortingFn: (rowA, rowB) => {
        const typeA = rowA.original.testType;
        const typeB = rowB.original.testType;
        if (typeA === 'WEB' && typeB !== 'WEB') {
          return -1;
        }
        if (typeA !== 'WEB' && typeB === 'WEB') {
          return 1;
        }
        return 0;
      },
      filterFn: 'equalsString',
      meta: {
        filterDisplayValue: {
          WEB: 'Web',
          WEB_MOBILE: 'Web Mobile',
          MOBILE_APP: 'Mobile App',
        },
        skeleton: () => (
          <Skeleton
            height={24}
            width={24}
            radius="sm"
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.updatedAt,
      id: 'updatedAt',
      header: 'Last Edited',
      size: 140,
      cell: ({ row }) =>
        row.original.updatedAt ?
          <TimeAgo
            time={row.original.updatedAt}
            textStyle="text-stone-600 dark:text-stone-400"
          />
        : undefined,
      sortingFn: 'datetime',
      sortUndefined: 'last',
      invertSorting: true,
      meta: {
        skeleton: () => (
          <Skeleton
            height={20}
            width={`${Math.random() * 20 + 100}px`}
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.latestTestRun?.createdAt,
      id: 'lastRan',
      header: 'Last Ran',
      size: 140,
      cell: ({ row }) =>
        row.original.latestTestRun?.createdAt ?
          <TimeAgo
            time={row.original.latestTestRun?.createdAt}
            textStyle="text-stone-600 dark:text-stone-400"
          />
        : undefined,
      sortingFn: 'datetime',
      sortUndefined: 'last',
      invertSorting: true,
      meta: {
        skeleton: () => (
          <Skeleton
            height={20}
            width={`${Math.random() * 20 + 100}px`}
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.tags,
      id: 'tags',
      header: 'Tags',
      cell: ({ row }) => (
        <TestTagCombobox
          test={row.original}
          tags={tags}
        />
      ),
      enableColumnFilter: true,
      filterFn: (row, id, filterValue) => {
        if (!row.original.tags || !filterValue) {
          return false;
        }
        return row.original.tags.some((tag) => tag.name === filterValue);
      },
      meta: {
        skeleton: () => (
          <Skeleton
            height={24}
            width={`${Math.random() * 100 + 50}px`}
            radius="xl"
          />
        ),
      },
    },
    ...(userFlows.length > 0 && tests?.some((test) => test.userFlows.length > 0) ? [userFlowsColumn] : []),
    {
      accessorFn: (row) => row.updatedByUserEmail,
      id: 'lastEditedBy',
      header: 'Last Edited By',
      size: 120,
      cell: ({ row }) =>
        row.original.updatedByUserEmail ? <CreatedBy email={row.original.updatedByUserEmail} /> : undefined,
      enableColumnFilter: true,
      sortUndefined: 'last',
      meta: {
        skeleton: () => (
          <Skeleton
            height={20}
            width={`${Math.random() * 20 + 80}px`}
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.createdByUserEmail,
      id: 'createdBy',
      header: 'Created By',
      size: 110,
      cell: ({ row }) =>
        row.original.createdByUserEmail ? <CreatedBy email={row.original.createdByUserEmail} /> : undefined,
      enableColumnFilter: true,
      sortUndefined: 'last',
      meta: {
        skeleton: () => (
          <Skeleton
            height={20}
            width={`${Math.random() * 20 + 80}px`}
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.testSuites,
      id: 'testSuites',
      header: 'Test Suites',
      cell: ({ row }) => <TestSuiteLabel test={row.original} />,
      filterFn: (row, id, filterValue: string) => {
        if (!row.original.testSuites || !filterValue) {
          return false;
        }
        return row.original.testSuites.some((testSuite) => testSuite.name === filterValue);
      },
      enableColumnFilter: true,
      meta: {
        skeleton: () => (
          <Skeleton
            height={24}
            width={`${Math.random() * 100 + 50}px`}
            radius="xl"
          />
        ),
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      enableSorting: false,
      size: 55,
      header: () => <div role="none" />,
      cell: ({ row }) => <ActionsCell row={row} />,
    },
  ];

  return baseColumns;
};
